<template>
  <AppNavbar />
  <AvisoSistemaNoListo />
  <div class="container mt-4" id="caja-view">
    <AppHeader title="Sistema de Caja" subtitle="Gestión de Tesorería">
      <template #buttons>
        <template v-if="cajaAbierta">
          <span class="badge bg-dark">Caja Abierta</span>
        </template>
      </template>
    </AppHeader>

    <!-- Acciones rápidas: siempre visibles cuando la caja está abierta -->
  <div v-if="cajaAbierta" class="quick-actions mt-3 mb-4">
      <router-link class="action-tile tile-success" to="/cobro-factura-venta" title="Cobrar pedidos publicados en caja">
        <div class="icon-circle"><i class="bi bi-graph-up"></i></div>
        <div class="tile-text">Cobro de Pedidos Publicados</div>
      </router-link>
      <router-link class="action-tile tile-danger" to="/pago-factura" title="Registrar pago a proveedores">
        <div class="icon-circle"><i class="bi bi-graph-down"></i></div>
        <div class="tile-text">Pago a Proveedores</div>
      </router-link>
      <router-link class="action-tile tile-primary" to="/ingreso-varios" title="Registrar ingreso varios">
        <div class="icon-circle"><i class="bi bi-plus-lg"></i></div>
        <div class="tile-text">Ingreso Varios</div>
      </router-link>
      <router-link class="action-tile tile-warning" to="/egreso-varios" title="Registrar egreso varios">
        <div class="icon-circle"><i class="bi bi-dash-lg"></i></div>
        <div class="tile-text">Egreso Varios</div>
      </router-link>
      <router-link class="action-tile tile-purple" to="/cobro-cliente-credito" title="Cobro a cliente con crédito">
        <div class="icon-circle"><i class="bi bi-credit-card"></i></div>
        <div class="tile-text">Cobro a Cliente Crédito</div>
      </router-link>
    </div>

    <!-- Se ve únicamente si la caja está CERRADA -->
    <div v-if="!cajaAbierta" class="text-center my-5">
      <div class="alert alert-info mx-auto" style="max-width: 500px;">
        <i class="bi bi-info-circle me-2"></i>
        <strong>Caja Cerrada</strong>
        <p class="mb-3 mt-2">{{ cashboxStore.contextMessage || 'No hay una caja abierta para el usuario actual. Debe abrir una caja para comenzar a realizar operaciones de tesorería.' }}</p>
      </div>
      <button class="btn btn-success btn-lg" @click="openModalCaja">
        <i class="bi bi-unlock me-2"></i>Abrir Caja
      </button>
    </div>


    <div v-if="cajaAbierta" class="card p-3 mb-3">
      <h5><i class="bi bi-clock me-2"></i>Sesión Actual</h5>
      <div class="row mt-2">
        <div class="col-md-3"><strong>Usuario</strong><br>{{ cajaData.user }}</div>
        <div class="col-md-3"><strong>Apertura</strong><br>{{ formatoFecha(cajaData.apertura) }}</div>
        <div class="col-md-3"><strong>Monto Inicial</strong><br>{{ formateaNumero(cajaData.montoInicial) }}</div>
        <div class="col-md-3 text-end">
            <router-link
                class="btn btn-outline-secondary"
                :to="{ name: 'CerrarCaja' }"
                >
                Cerrar Caja
            </router-link>
        </div>
      </div>
    </div>

    <div v-if="cajaAbierta" class="row mb-4">
      <div class="col-md-3">
        <div class="card text-center p-3">
          <div class="text-success">Ingresos del Día</div>
          <h5 class="text-success">{{ formateaNumero(cajaData.ingresos_dia) }}</h5>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center p-3">
          <div class="text-danger">Egresos del Día</div>
          <h5 class="text-danger">{{ formateaNumero(cajaData.egresos_dia) }}</h5>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center p-3">
          <div class="text-primary">Saldo en Caja</div>
          <h5 class="text-primary">{{ formateaNumero(cajaData.saldo_en_caja) }}</h5>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card text-center p-3">
          <div class="text-warning">Pendientes de esta Caja</div>
          <h5 class="text-warning">{{ cajaData.facturas_pendientes }}</h5>
        </div>
      </div>
    </div>
    <!-- Ventas a Crédito Pendientes de Cobro -->
    <div v-if="cajaAbierta && creditosPendientes.length" class="mb-4">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h5 class="fw-bold mb-0 text-warning">
          <i class="bi bi-clock-history me-2"></i>Créditos pendientes de cobro
          <span class="badge bg-warning text-dark ms-2">{{ creditosPendientes.length }}</span>
        </h5>
      </div>
      <div v-for="cr in creditosPendientes" :key="cr.id" class="border border-warning rounded p-3 mb-2 d-flex align-items-center justify-content-between">
        <div>
          <div class="fw-semibold">{{ cr.nombre || 'Cliente sin nombre' }}</div>
          <div class="text-muted small">
            {{ cr.comprobante ? `Comprobante: ${cr.comprobante}` : `Mov. #${cr.id}` }}
            · {{ formatFechaHora(cr.fecha) }}
          </div>
        </div>
        <div class="d-flex align-items-center gap-3">
          <div class="fw-bold text-warning">Gs. {{ formateaNumero(cr.monto) }}</div>
          <!-- RN-AN39: sólo con el permiso (no se muestra deshabilitado). -->
          <button
            v-if="puedeAnular"
            class="btn btn-outline-danger btn-sm"
            :disabled="anulando === cr.id"
            @click="confirmarAnularCredito(cr)"
          >
            <span v-if="anulando === cr.id" class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>
            <i v-else class="bi bi-x-circle me-1"></i>Anular
          </button>
        </div>
      </div>
    </div>

    <AnulacionVentaModal
      v-if="anulacionModalCredito"
      :nombre-cliente="anulacionModalCredito.nombre"
      :monto="anulacionModalCredito.monto"
      :enviando="anulando === anulacionModalCredito.id"
      @cerrado="anulacionModalCredito = null"
      @confirmada="ejecutarAnulacionCredito"
    />

    <!-- Movimientos Recientes -->
    <div v-if="cajaAbierta" class="mb-4">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h5 class="fw-bold mb-0">Movimientos Recientes</h5>
        <router-link class="btn btn-dark" :to="{ name: 'MovimientosCaja' }">Ver Todos</router-link>
      </div>
      <div v-for="mov in movimientosRecientes" :key="mov.id" class="recent-mov-card mb-2 p-3 d-flex align-items-center justify-content-between rounded border">
        <div class="d-flex align-items-center">
            <span v-if="isPositive(mov)" class="me-2 fs-4" :class="getMovementIconClass(mov)"><i class="bi bi-graph-up"></i></span>
            <span v-else-if="isNegative(mov)" class="me-2 fs-4" :class="getMovementIconClass(mov)"><i class="bi bi-graph-down"></i></span>
            <span v-else class="me-2 fs-4" :class="getMovementIconClass(mov)"></span>
          <div>
              <span class="fw-bold" :class="getMovementIconClass(mov)">
                {{ movLabel(mov) }}
                <span v-if="mov.comprobante">#{{ mov.comprobante }}</span>
              </span>
            <span v-if="mov.comprobante" class="badge bg-light text-dark ms-2">Nro mov caja: {{ mov.id }}</span>
            <div class="text-muted small">{{ mov.descripcion }}</div>
          </div>
        </div>
        <div class="text-end">
          <div :class="getMovementIconClass(mov) + ' fw-bold'">
            {{ isPositive(mov) ? '+' : (isNegative(mov) ? '-' : '') }}Gs. {{ formateaNumero(mov.monto) }}
          </div>
          <div class="text-muted small">{{ formatFechaHora(mov.fecha) }}</div>
        </div>
      </div>
    </div>
    <div class="modal fade"
        :class="{ show: modalOpen }"
        :style="{ display: modalOpen ? 'block' : 'none' }"
        tabindex="-1" role="dialog" aria-modal="true">
      <div class="modal-dialog">
        
        <div class="modal-content">
          <form @submit.prevent="abrirCaja">
            <!-- Cabecera -->
            <div class="modal-header">
              <h5 class="modal-title">Apertura de Caja</h5>
              <button type="button" class="btn-close" @click="closeModalCaja" />
            </div>

            <!-- Cuerpo -->
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label" for="cashboxOpeningSucursal">Sucursal</label>
                <select id="cashboxOpeningSucursal" class="form-select" v-model.number="openForm.sucursalId" @change="handleSucursalChange" required>
                  <option v-for="sucursal in openingContext.sucursales" :key="'sucursal-'+sucursal.sucursal_id" :value="sucursal.sucursal_id">
                    {{ sucursal.nombre_establecimiento }} ({{ sucursal.sucursal_id }})
                  </option>
                </select>
              </div>

              <div class="mb-3">
                <label class="form-label" for="cashboxOpeningCaja">Caja</label>
                <select id="cashboxOpeningCaja" class="form-select" v-model.number="openForm.cajaId" required>
                  <option v-for="caja in openingContext.cajas" :key="'caja-'+caja.caja_id" :value="caja.caja_id">
                    {{ cajaOptionLabel(caja) }}
                  </option>
                </select>
                <small v-if="openForm.sucursalId && !openingContext.cajas.length" class="text-danger">
                  No hay cajas activas configuradas para esta sucursal habilitada.
                </small>
              </div>

              <div class="mb-3">
                <label class="form-label" for="cashboxOpeningDate">Fecha y hora</label>
                <input type="datetime-local"
                      id="cashboxOpeningDate"
                      class="form-control"
                      v-model="openForm.fechaApertura"
                      required />
              </div>

              <div class="mb-3">
                <label class="form-label" for="cashboxOpeningAmount">Monto inicial</label>
                <MoneyInput
                      input-id="cashboxOpeningAmount"
                      v-model="openForm.montoInicial"
                      placeholder="0"
                      emit-zero-when-empty />
              </div>

              <div class="mb-3">
                <label class="form-label" for="cashboxOpeningObservation">Observación (opcional)</label>
                <input type="text"
                      id="cashboxOpeningObservation"
                      class="form-control"
                      v-model="openForm.observacion" />
              </div>
            </div>

            <!-- Pie -->
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeModalCaja">
                Cancelar
              </button>
              <button type="submit" class="btn btn-primary" :disabled="!canOpenCashbox">
                Abrir Caja
              </button>
            </div>
          </form>
        </div>
        
      </div>
    </div>
    <div v-if="modalOpen" class="modal-backdrop fade show"></div>

    
  </div>
</template>

<script setup>
import AvisoSistemaNoListo from '@/components/AvisoSistemaNoListo.vue'
import MoneyInput from '@/components/MoneyInput.vue'
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue'
import AppNavbar from '@/components/AppNavbar.vue'
import AppHeader from '@/components/AppHeader.vue'
import AnulacionVentaModal from '@/components/AnulacionVentaModal.vue'
import apiService from '@/services/apiService'
import { hasPermission } from '@/services/authSessionService'
import { textoInstrumento } from '@/services/anulacionService'
import operationalConfigService from '@/services/operationalConfigService'
import operationalContextService from '@/services/operationalContextService'
import { useCashboxStore } from '@/stores/cashbox'

// Movimientos recientes
const movimientosRecientes = ref([])
// Créditos pendientes de cobro en la sesión actual
const creditosPendientes = ref([])
const anulando = ref(null)
// Venta (cr) que se está anulando vía AnulacionVentaModal — null = modal cerrado.
const anulacionModalCredito = ref(null)
const puedeAnular = computed(() => hasPermission('gestionar_anulaciones'))

async function fetchMovimientosRecientes() {
  try {
    const res = await apiService.get('/api/cashbox/user-operations?page=1&limit=5')
    if (res.data && res.data.success && Array.isArray(res.data.data)) {
      movimientosRecientes.value = res.data.data.map(mov => ({
        ...mov,
        monto: Number(mov.monto),
        fecha: mov.fecha
      }))
    }
  } catch (err) {
    console.error('Error al obtener movimientos recientes:', err)
  }
}

async function fetchCreditosPendientes() {
  try {
    const res = await apiService.get('/api/cashbox/user-operations?tipo_movimiento=COBRO&estado=PENDIENTE&page=1&limit=50')
    if (res.data && res.data.success && Array.isArray(res.data.data)) {
      creditosPendientes.value = res.data.data
        .filter(m => m.forma === 'CREDITO')
        .map(m => ({ ...m, monto: Number(m.monto) }))
    }
  } catch (err) {
    console.error('Error al obtener créditos pendientes:', err)
  }
}

// Abre AnulacionVentaModal en vez de window.prompt (que no puede pedir dos
// datos: motivo + si la mercadería volvió físicamente). Backend exige
// `devolvio_mercaderia` explícito desde SPEC_DEVOLUCIONES_v0.1.md RF5.
function confirmarAnularCredito(cr) {
  anulacionModalCredito.value = cr
}

async function ejecutarAnulacionCredito({ motivo, devolvio_mercaderia }) {
  const cr = anulacionModalCredito.value
  if (!cr) return

  anulando.value = cr.id
  try {
    const { data } = await apiService.post('/api/cashbox/cancel-credit-sale', {
      movimiento_id: cr.id,
      motivo,
      devolvio_mercaderia
    })
    // D12: Caja muestra cómo se resolvió la factura ante la SET.
    const r = data?.data?.resolucion_fiscal
    const texto = textoInstrumento(r?.instrumento, r?.numero_nota_credito)
    if (texto) window.alert.success?.(`Venta anulada. ${texto}.`, { title: 'Anulación' })
    creditosPendientes.value = creditosPendientes.value.filter(c => c.id !== cr.id)
    anulacionModalCredito.value = null
    await fetchMovimientosRecientes()
  } catch (err) {
    const msg = err?.response?.data?.message || err?.message || 'Error al anular la venta'
    alert(msg)
    console.error('Error al anular crédito:', err)
  } finally {
    anulando.value = null
  }
}




const cashboxStore = useCashboxStore()
const cajaAbierta = computed(() => cashboxStore.isOpen)
const canOpenCashbox = computed(() =>
  Boolean(openForm.sucursalId && openForm.cajaId && openForm.fechaApertura && openForm.montoInicial !== null)
)
const cajaData    = computed(() => ({
  user        : cashboxStore.nombreUsuario,
  apertura    : cashboxStore.fechaApertura,
  montoInicial: cashboxStore.montoInicial,
  ingresos_dia: cashboxStore.igresos_dia,
  egresos_dia : cashboxStore.egresos_dia,
  saldo_en_caja: cashboxStore.saldo_en_caja,
  facturas_pendientes: cashboxStore.facturas_pendientes
}))
onMounted(() => {
  cashboxStore.fetchCurrentOpen({ sucursal_id: operationalConfigService.getCurrentSucursalId() })
  fetchMovimientosRecientes()
  fetchCreditosPendientes()
  window.addEventListener('app:sucursal-changed', handleGlobalSucursalChange)
})

onBeforeUnmount(() => {
  window.removeEventListener('app:sucursal-changed', handleGlobalSucursalChange)
})

function handleGlobalSucursalChange(event) {
  const sucursalId = Number(event?.detail?.sucursal_id || operationalConfigService.getCurrentSucursalId())
  cashboxStore.resetContext('Contexto de caja limpiado por cambio de establecimiento. Abra una caja para operar caja en este establecimiento.')
  cashboxStore.fetchCurrentOpen({ sucursal_id: sucursalId })
  fetchMovimientosRecientes()
  fetchCreditosPendientes()
  creditosPendientes.value = []
}

const modalOpen   = ref(false)
const openingContext = reactive({
  sucursales: [],
  cajas: []
})

const openForm = reactive({
  sucursalId   : null,
  cajaId       : null,
  fechaApertura: '',
  montoInicial : null,
  moneda       : 'PYG',
  observacion  : ''
})




async function loadOpeningContext(sucursalId = null) {
  const context = await operationalContextService.getCashboxOpeningContext(
    sucursalId ? { sucursal_id: sucursalId } : {}
  )
  openingContext.sucursales = Array.isArray(context?.sucursales) ? context.sucursales : []
  openingContext.cajas = Array.isArray(context?.cajas) ? context.cajas : []
  openForm.sucursalId = Number(context?.sucursal_id || openingContext.sucursales[0]?.sucursal_id || null)
  if (!openingContext.cajas.some((item) => Number(item.caja_id) === Number(openForm.cajaId))) {
    openForm.cajaId = Number(openingContext.cajas[0]?.caja_id || null)
  }
  if (openForm.sucursalId) {
    localStorage.setItem('sucursal_id', String(openForm.sucursalId))
  }
}

async function handleSucursalChange() {
  await loadOpeningContext(openForm.sucursalId)
}

function openModalCaja () {
  openForm.fechaApertura = nowLocalISO() // yyyy-MM-ddTHH:mm
  openForm.montoInicial  = null
  openForm.observacion   = ''
  openForm.sucursalId    = operationalConfigService.getCurrentSucursalId()
  openForm.cajaId        = null
  loadOpeningContext(openForm.sucursalId)
    .then(() => {
      modalOpen.value = true
    })
    .catch((err) => {
      alert(err?.response?.data?.message || err?.response?.data?.error || 'No se pudo cargar el contexto de apertura de caja')
    })
}

function closeModalCaja () {
  modalOpen.value = false
}

function cajaOptionLabel(caja) {
  const point = caja?.codigo_punto_expedicion ? ` - Punto ${caja.codigo_punto_expedicion}` : ''
  return `${caja?.codigo_caja || 'Caja'} - ${caja?.nombre || ''}${point}`
}




async function abrirCaja () {
  try {
    await apiService.post('/api/cashbox/open', {
      fecha_apertura: openForm.fechaApertura,
      monto_inicial : openForm.montoInicial,
      moneda        : openForm.moneda,
      observacion   : openForm.observacion,
      sucursal_id   : openForm.sucursalId,
      caja_id       : openForm.cajaId
    })
    // refrescar estado global
    await cashboxStore.fetchCurrentOpen()
    modalOpen.value = false
  } catch (err) {
    console.error(err)
    alert(err?.response?.data?.message || err?.response?.data?.error || 'Error al abrir caja')
  }
}


// (Se removió el dropdown; acciones ahora están siempre visibles)

/* ---------- helpers de formato ---------- */
function formatoFecha (val) {
  if (!val) return ''
  return new Date(val).toLocaleString('es-PY')   // hora local – formato PY
}
function nowLocalISO () {
  const now    = new Date()
  const offset = now.getTimezoneOffset() * 60000 // min → ms
  const local  = new Date(now - offset)
  return local.toISOString().slice(0, 16)        // YYYY-MM-DDTHH:mm
}

function formateaNumero (n) {
  if (n === null || n === undefined) return ''
  return Number(n).toLocaleString('es-PY', { minimumFractionDigits: 0 })
}

function formatFechaHora(val) {
  if (!val) return ''
  const d = new Date(val)
  return d.toLocaleString('es-PY')
}

// helpers for movement display
function isPositive(mov) {
  // COBRO and INGRESO_VARIO are considered positive (green)
  return mov.tipo_movimiento === 'COBRO' || mov.tipo_movimiento === 'INGRESO_VARIO'
}

function isNegative(mov) {
  // PAGO and EGRESO_VARIO are considered negative (red)
  return mov.tipo_movimiento === 'PAGO' || mov.tipo_movimiento === 'EGRESO_VARIO'
}

/*function isNeutral(mov) {
  // APERTURA is considered neutral
  return mov.tipo_movimiento === 'APERTURA'
}*/

function getMovementIconClass(mov) {
  switch (mov.tipo_movimiento) {
    case 'COBRO':
    case 'INGRESO_VARIO':
      return 'text-success'
    case 'PAGO':
    case 'EGRESO_VARIO':
      return 'text-danger'
    case 'APERTURA':
      return 'text-secondary'
    default:
      return isPositive(mov) ? 'text-success' : 'text-danger'
  }
}

function movLabel(mov) {
  if (mov.tipo_movimiento === 'COBRO') return mov.tipo_operacion === 'VENTA' ? 'Cobro Factura' : 'Cobro'
  if (mov.tipo_movimiento === 'INGRESO_VARIO') return 'Ingreso Varios'
  if (mov.tipo_movimiento === 'PAGO') return 'Pago a Proveedores'
  if (mov.tipo_movimiento === 'EGRESO_VARIO') return 'Egreso Varios'
  if (mov.tipo_movimiento === 'APERTURA') return 'Apertura de Caja'
  return mov.tipo_movimiento
}


</script>

<style scoped>
.recent-mov-card {
  background: #fff;
  border: 1px solid #e5e5e5;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  transition: box-shadow 0.2s;
}
.recent-mov-card:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}
</style>

<style scoped>
#caja-view .card {
  border-radius: 0.5rem;
}
#caja-view .nav-link.active {
  font-weight: bold;
}

/* Acciones rápidas como “tarjetas/botones” */
.quick-actions {
  display: grid;
  gap: 14px;
  grid-template-columns: 1fr;
}
@media (min-width: 576px) {
  .quick-actions { grid-template-columns: repeat(2, 1fr); }
}
@media (min-width: 768px) {
  .quick-actions { grid-template-columns: repeat(3, 1fr); }
}
@media (min-width: 992px) {
  .quick-actions { grid-template-columns: repeat(4, 1fr); }
}
@media (min-width: 1200px) {
  .quick-actions { grid-template-columns: repeat(5, 1fr); }
}

.action-tile {
  /* variables por defecto */
  --tile-bg: #f8f9fa;
  --tile-border: #e5e5e5;
  --tile-text: #212529;
  --tile-icon-bg: #eef1f4;

  background: var(--tile-bg);
  border: 1px solid var(--tile-border);
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  padding: 18px 16px;
  height: 96px;
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: var(--tile-text);
  transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease;
}
.action-tile:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  text-decoration: none;
  border-color: rgba(0,0,0,0.08);
}
.action-tile:focus-visible {
  outline: 3px solid rgba(13,110,253,.35);
  outline-offset: 2px;
}

.icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--tile-icon-bg);
  font-size: 1.1rem;
}

.tile-text {
  font-weight: 600;
  line-height: 1.2;
}

/* Variantes “soft” */
.tile-success {
  --tile-bg: #f3fcf5;
  --tile-border: #d7f0dc;
  --tile-text: #146c43;
  --tile-icon-bg: #e7f8ea;
}
.tile-danger {
  --tile-bg: #fff4f4;
  --tile-border: #f5d6d6;
  --tile-text: #b02a37;
  --tile-icon-bg: #ffe9e9;
}
.tile-primary {
  --tile-bg: #f2f6ff;
  --tile-border: #d7e3ff;
  --tile-text: #0d6efd;
  --tile-icon-bg: #e7efff;
}
.tile-warning {
  --tile-bg: #fff9ed;
  --tile-border: #ffe4b8;
  --tile-text: #b68200;
  --tile-icon-bg: #fff0cc;
}
.tile-purple {
  --tile-bg: #f7f2ff;
  --tile-border: #e4d6ff;
  --tile-text: #6f42c1;
  --tile-icon-bg: #eee4ff;
}

.text-purple { color: #6f42c1; }
</style>
